import React from "react";
import { UISelectorType } from "../../utilities/types";
import { BaseCustomizationEntry } from "../customizeEntries";
import { isEntryDisabled, isEntryHidden } from "./optionValue";
import { DefaultSelector } from "./defaultSelector";
import { BooleanSelector } from "./booleanSelector";
import { SwitchSelector } from "./switchSelector";
import { IntegerSelector } from "./integerSelector";
import { PercentageSelector } from "./percentageSelector";
import { ColorSelector } from "./colorSelector";
import { RgbaSelector } from './rgbaSelector'

import { mergeStyles } from "@fluentui/react";
import { DropDownSelector } from "./dropDownSelector";
import { ContainerName } from "../../Redux/reducers/AppReducer";
import { DEFAULT_UNDEFINED_RGBA } from "../constants/defaultStyleOptions";
import { ReactSelector } from "./reactSelector";
import { LocalizationSelector } from "./localizationSelector";
import ThemeSelector from "./themeSelector";
import { AvatarSelector } from "./avatarSelector";


const UI_selector = mergeStyles(
    {
        fontSize: '15px',
        fontWeight: '400',
        paddingBottom: 15
    }
);

const highlightedLabel = mergeStyles(
    {
        color: 'var(--druid-color-success-primary)',
        transition: 'color var(--druid-transition-base) ease',
    }
);

const disabledEntry = mergeStyles(
    {
        opacity: 0.5,
        pointerEvents: 'none',
    }
);

interface CustomizationEntrySelectorProps {
    entry: BaseCustomizationEntry;
    value: any;
    onChange: (styleElementName: string, value: any, containerName?: ContainerName) => void;
    getStyleOptionValue: (key: any, containerName?: ContainerName) => any;
    highlighted?: boolean;
}

export const CustomizationEntrySelector = (props: CustomizationEntrySelectorProps) => {
    const { entry, value, onChange, highlighted } = props;
    const { displayName, id } = entry;

    if (isEntryHidden(entry, props.getStyleOptionValue)) {
        return null;
    }

    const entryIsDisabled = isEntryDisabled(entry, props.getStyleOptionValue);

    const onChangeWithContainerName = (styleElementName: string, value: any) => {
        props.onChange(styleElementName, value, entry.containerName);
    }

    const renderSelector = (type: UISelectorType) => {
        switch (type) {
            case UISelectorType.booleanSelector:
                return <BooleanSelector id={id} label={displayName} onChange={onChangeWithContainerName} value={value} highlightClassName={highlighted ? highlightedLabel : undefined} />
            case UISelectorType.switchSelector:
                return <SwitchSelector id={id} label={displayName} onChange={onChangeWithContainerName} value={value} highlightClassName={highlighted ? highlightedLabel : undefined} />
            case UISelectorType.integerSelector:
                return <IntegerSelector id={id} onChange={onChangeWithContainerName} value={value} />
            case UISelectorType.percentageSelector:
                return <PercentageSelector id={id} onChange={onChangeWithContainerName} value={value} />
            case UISelectorType.colorSelector:
                return <ColorSelector id={id} onChange={onChangeWithContainerName} value={value} getStyleOptionValue={props.getStyleOptionValue} />
            case UISelectorType.dropDownSelector:
                return <DropDownSelector id={id} onChange={onChangeWithContainerName} value={value} />
            case UISelectorType.rgbaSelector:
                return <RgbaSelector id={id} onChange={onChangeWithContainerName} value={value || DEFAULT_UNDEFINED_RGBA} />
            case UISelectorType.reactSelector:
                return <ReactSelector id={id} onChange={onChangeWithContainerName} value={value || ""} defaultValue={entry.defaultValue} />
            case UISelectorType.localizationSelector:
                return <LocalizationSelector
                    id={id} onChange={onChangeWithContainerName}
                    value={value || ""} defaultValue={entry.defaultValue}
                    languages={props.getStyleOptionValue("languages", "root")}
                    isVoice={entry.containerName === "VoiceUI"}
                    />
            case UISelectorType.themeSelector:
                return <ThemeSelector id={id} value={value} onChange={onChangeWithContainerName} />
            case UISelectorType.avatarSelector:
                return <AvatarSelector id={id} value={value} onChange={onChangeWithContainerName} />
            case UISelectorType.defaultSelector:
            default:
                return <DefaultSelector multiline={entry.multiline} id={id} onChange={onChangeWithContainerName} value={value} defaultValue={entry.defaultValue}/>
        }
    }

    const isBespokePanel = entry.uiSelectorType === UISelectorType.themeSelector
        || entry.uiSelectorType === UISelectorType.switchSelector
        || entry.uiSelectorType === UISelectorType.booleanSelector
        || entry.uiSelectorType === UISelectorType.avatarSelector;

    return (
        <div className={`${UI_selector}${entryIsDisabled ? ` ${disabledEntry}` : ''}`} data-entry-id={id} aria-disabled={entryIsDisabled || undefined}>
            {!isBespokePanel &&
                <span className={highlighted ? highlightedLabel : undefined}>
                    <strong>{displayName} </strong>({id})
                </span>}

            {renderSelector(entry.uiSelectorType)}
        </div>
    );
}
